import type { FC } from "react";
import { useState, useCallback } from "react";
import { View, type LayoutChangeEvent } from "react-native";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Text, IconButton, Icon, Skeleton, SkeletonGroup } from "@visioni/a3s-ui/components";

export type CompilationRowStatus = "ready" | "processing" | "queued" | "uploading" | "failed" | "expired";

export interface CompilationRowProps {
  readonly status: CompilationRowStatus;
  /**
   * Marks a `queued` job that has waited long enough to probably never have
   * been picked up. Renders a warning alongside the status rather than
   * changing it — the job really is still queued as far as the server is
   * concerned, and it may yet run.
   */
  readonly staleQueued?: boolean | undefined;
  /**
   * Why the compilation failed, in prose. Shown only alongside a `failed`
   * status — an operator seeing "Failed" with no explanation has nothing to
   * act on, and most of these reasons are things they can fix themselves.
   */
  readonly failureReason?: string | undefined;
  /** Display name of the camera the compilation was requested for. */
  readonly cameraLabel: string;
  /** When the compilation was requested (ISO string or formatted display string). */
  readonly requestedAt: string;
  /** Start of the requested compilation range (display string). */
  readonly rangeStart: string;
  /** End of the requested compilation range (display string). */
  readonly rangeEnd: string;
  readonly onWatch?: (() => void) | undefined;
  readonly onDownload?: (() => void) | undefined;
}

const statusConfig: Record<CompilationRowStatus, { readonly label: string; readonly dotColor: string }> = {
  ready: { label: "Ready", dotColor: "#10B981" },
  processing: { label: "Processing", dotColor: "#3B82F6" },
  queued: { label: "Queued", dotColor: "#3B82F6" },
  uploading: { label: "Uploading", dotColor: "#3B82F6" },
  failed: { label: "Failed", dotColor: "#EF4444" },
  expired: { label: "Expired", dotColor: "#94A3B8" },
};

const WIDE_BREAKPOINT = 500;

/** Deliberately hedged — the job is still queued and may yet be picked up. */
const STALE_QUEUED_WARNING = "May not have been received";

const CompilationRow: FC<CompilationRowProps> = ({
  status,
  staleQueued,
  failureReason,
  cameraLabel,
  requestedAt,
  rangeStart,
  rangeEnd,
  onWatch,
  onDownload,
}) => {
  const { theme } = useUnistyles();
  const { label, dotColor } = statusConfig[status];
  const actionsEnabled = status === "ready";
  // A ready job can still lack a download URL (e.g. a not-yet-redeployed
  // Lambda that only signs `url`) — `onDownload` is only passed by the caller
  // when one exists, so the button disables itself rather than being a dead
  // no-op click.
  const watchEnabled = actionsEnabled && !!onWatch;
  const downloadEnabled = actionsEnabled && !!onDownload;
  const [wide, setWide] = useState(false);

  const handleLayout = useCallback((e: LayoutChangeEvent) => {
    setWide(e.nativeEvent.layout.width >= WIDE_BREAKPOINT);
  }, []);

  // Only meaningful next to a queued status — a job that has since moved on
  // carries its own status, and the warning would contradict it.
  const staleWarning =
    staleQueued === true && status === "queued" ?
      <View
        style={[styles.warningBubble, { borderColor: theme.colors.status.warning }]}
        accessibilityRole="alert"
        accessibilityLabel={STALE_QUEUED_WARNING}
      >
        <Icon name="warningOutline" size={12} color={theme.colors.status.warning} />
        <Text textStyle="xs/medium" style={{ color: theme.colors.status.warning }} numberOfLines={1}>
          {STALE_QUEUED_WARNING}
        </Text>
      </View>
    : null;

  const failureNote =
    failureReason && status === "failed" ?
      <View
        style={[styles.warningBubble, { borderColor: theme.colors.danger[400] }]}
        accessibilityRole="alert"
        accessibilityLabel={failureReason}
      >
        <Icon name="warningOutline" size={12} color={theme.colors.danger[400]} />
        <Text textStyle="xs/medium" style={{ color: theme.colors.danger[400] }} numberOfLines={2}>
          {failureReason}
        </Text>
      </View>
    : null;

  const actions = (
    <View style={styles.actionsCell}>
      <IconButton
        Icon={() => <Icon name="play" size={16} color={theme.colors.text} />}
        title="Watch compilation"
        disabled={!watchEnabled}
        size="sm"
        style={!watchEnabled ? styles.actionButtonDisabled : undefined}
        {...(watchEnabled ? { onPress: () => onWatch?.() } : {})}
      />
      <IconButton
        Icon={() => <Icon name="download" size={16} color={theme.colors.text} />}
        title="Download compilation"
        disabled={!downloadEnabled}
        size="sm"
        style={!downloadEnabled ? styles.actionButtonDisabled : undefined}
        {...(downloadEnabled ? { onPress: () => onDownload?.() } : {})}
      />
    </View>
  );

  if (wide) {
    return (
      <View style={styles.containerWide} onLayout={handleLayout}>
        <View style={styles.statusCell}>
          <View style={[styles.dot, { backgroundColor: dotColor }]} />
          <Text textStyle="sm/medium" color="text">
            {label}
          </Text>
        </View>

        <View style={styles.cameraCell}>
          <Text textStyle="sm/regular" color="text" numberOfLines={1}>
            {cameraLabel}
          </Text>
        </View>

        <View style={styles.detailsCellWide}>
          <Text textStyle="sm/regular" color="text" numberOfLines={1}>
            {rangeStart} – {rangeEnd}
          </Text>
          <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
            Requested {requestedAt}
          </Text>
          {staleWarning}
          {failureNote}
        </View>

        {actions}
      </View>
    );
  }

  return (
    <View style={styles.container} onLayout={handleLayout}>
      <View style={styles.topRow}>
        <View style={styles.statusCell}>
          <View style={[styles.dot, { backgroundColor: dotColor }]} />
          <Text textStyle="sm/medium" color="text">
            {label}
          </Text>
        </View>
        {actions}
      </View>

      <View style={styles.detailsCell}>
        <Text textStyle="sm/medium" color="text" numberOfLines={1}>
          {cameraLabel}
        </Text>
        <View style={styles.dateGroup}>
          <Text textStyle="sm/regular" color="text" numberOfLines={1}>
            {rangeStart} - {rangeEnd}
          </Text>
          <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
            Requested {requestedAt}
          </Text>
          {staleWarning}
          {failureNote}
        </View>
      </View>
    </View>
  );
};

CompilationRow.displayName = "CompilationRow";

export { CompilationRow };

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

const CompilationRowSkeleton: FC = () => {
  const [wide, setWide] = useState(false);

  const handleLayout = useCallback((e: LayoutChangeEvent) => {
    setWide(e.nativeEvent.layout.width >= WIDE_BREAKPOINT);
  }, []);

  if (wide) {
    return (
      <SkeletonGroup>
        <View style={styles.containerWide} onLayout={handleLayout}>
          <View style={styles.statusCell}>
            <Skeleton variant="circular" width={8} height={8} />
            <Skeleton variant="text" width={70} textStyle="sm/medium" />
          </View>
          <View style={styles.cameraCell}>
            <Skeleton variant="text" width="70%" textStyle="sm/regular" />
          </View>
          <View style={styles.detailsCellWide}>
            <Skeleton variant="text" width="80%" textStyle="sm/regular" />
            <Skeleton variant="text" width="50%" textStyle="xs/regular" />
          </View>
          <View style={styles.actionsCell}>
            <Skeleton variant="rounded" width={28} height={28} />
            <Skeleton variant="rounded" width={28} height={28} />
          </View>
        </View>
      </SkeletonGroup>
    );
  }

  return (
    <SkeletonGroup>
      <View style={styles.container} onLayout={handleLayout}>
        <View style={styles.topRow}>
          <View style={styles.statusCell}>
            <Skeleton variant="circular" width={8} height={8} />
            <Skeleton variant="text" width={70} textStyle="sm/medium" />
          </View>
          <View style={styles.actionsCell}>
            <Skeleton variant="rounded" width={28} height={28} />
            <Skeleton variant="rounded" width={28} height={28} />
          </View>
        </View>
        <View style={styles.detailsCell}>
          <Skeleton variant="text" width="60%" textStyle="sm/medium" />
          <View style={styles.dateGroup}>
            <Skeleton variant="text" width="80%" textStyle="sm/regular" />
            <Skeleton variant="text" width="50%" textStyle="xs/regular" />
          </View>
        </View>
      </View>
    </SkeletonGroup>
  );
};

CompilationRowSkeleton.displayName = "CompilationRowSkeleton";

export { CompilationRowSkeleton };

const styles = StyleSheet.create((theme) => ({
  container: {
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.lg,
    backgroundColor: theme.colors.surfaceCard,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    gap: theme.spacing.md,
  },
  containerWide: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.lg,
    backgroundColor: theme.colors.surfaceCard,
    borderRadius: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    gap: theme.spacing.xl,
  },
  topRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  statusCell: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
    minWidth: 100,
  },
  cameraCell: {
    minWidth: 120,
    maxWidth: 220,
    flex: 0.5,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  detailsCell: {
    gap: theme.spacing.xs,
  },
  dateGroup: {
    gap: theme.spacing.xxs,
  },
  detailsCellWide: {
    flex: 1,
    minWidth: 0,
    gap: theme.spacing.xxs,
  },
  actionsCell: {
    flexDirection: "row",
    gap: theme.spacing.xs,
  },
  actionButtonDisabled: {
    opacity: 0.35,
  },
  // `alignSelf` keeps the bubble hugging its text instead of stretching to
  // the full width of the details column. Border colour is applied inline
  // from the theme's warning token.
  warningBubble: {
    alignSelf: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.xs,
    marginTop: theme.spacing.xs,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xxs,
    borderRadius: theme.spacing.lg,
    borderWidth: 1,
  },
}));
